import { useEffect, useMemo, useState } from "react";
import type { Entry, Layer, MapSummary } from "shared";
import { layersApi } from "@/api/layers";
import { mapsApi } from "@/api/maps";
import { Button } from "@/components/primitives/Button";
import { Dialog, DialogActions } from "@/components/primitives/Dialog";

type Props = {
	open: boolean;
	entry: Entry;
	currentMapId: string;
	currentLayers: Layer[];
	onClose: () => void;
	onMove: (target: { mapId: string; layerId: string; layerTitle: string }) => Promise<void>;
};

const selectClass =
	"h-9 w-full rounded-[var(--radius-control)] bg-surface px-3 text-sm text-fg ring-1 ring-[var(--ring-hairline)] outline-none focus:ring-2 focus:ring-teal-500";

export function MoveEntryDialog({
	open,
	entry,
	currentMapId,
	currentLayers,
	onClose,
	onMove,
}: Props) {
	const [maps, setMaps] = useState<MapSummary[]>([]);
	const [mapsLoading, setMapsLoading] = useState(false);
	const [targetMapId, setTargetMapId] = useState(currentMapId);
	const [targetLayers, setTargetLayers] = useState<Layer[]>([]);
	const [layersLoading, setLayersLoading] = useState(false);
	const [targetLayerId, setTargetLayerId] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!open) return;
		setTargetMapId(currentMapId);
		setError(null);
		setSaving(false);
		setMapsLoading(true);
		void mapsApi
			.list()
			.then((res) => {
				const list = res.maps;
				if (!list.some((m) => m.id === currentMapId)) {
					setMaps([
						{
							id: currentMapId,
							ownerId: "",
							title: "Diese Karte",
							description: null,
							visibility: "private",
							baseStyle: "osm",
							viewState: null,
							createdAt: "",
							updatedAt: "",
						},
						...list,
					]);
				} else {
					setMaps(list);
				}
			})
			.catch((e) => {
				setError(e instanceof Error ? e.message : "Karten laden fehlgeschlagen");
				setMaps([
					{
						id: currentMapId,
						ownerId: "",
						title: "Diese Karte",
						description: null,
						visibility: "private",
						baseStyle: "osm",
						viewState: null,
						createdAt: "",
						updatedAt: "",
					},
				]);
			})
			.finally(() => setMapsLoading(false));
	}, [open, currentMapId]);

	useEffect(() => {
		if (!open) return;

		if (targetMapId === currentMapId) {
			setTargetLayers(currentLayers);
			const firstOther =
				currentLayers.find((l) => l.id !== entry.layerId)?.id ?? "";
			setTargetLayerId(firstOther);
			setLayersLoading(false);
			return;
		}

		setLayersLoading(true);
		setTargetLayerId("");
		void layersApi
			.list(targetMapId)
			.then((res) => {
				setTargetLayers(res.layers);
				setTargetLayerId(res.layers[0]?.id ?? "");
			})
			.catch((e) => {
				setError(e instanceof Error ? e.message : "Ebenen laden fehlgeschlagen");
				setTargetLayers([]);
				setTargetLayerId("");
			})
			.finally(() => setLayersLoading(false));
	}, [open, targetMapId, currentMapId, currentLayers, entry.layerId]);

	const selectableLayers = useMemo(() => {
		if (targetMapId === currentMapId) {
			return targetLayers.filter((l) => l.id !== entry.layerId);
		}
		return targetLayers;
	}, [targetLayers, targetMapId, currentMapId, entry.layerId]);

	const canSubmit =
		Boolean(targetLayerId) &&
		!saving &&
		!layersLoading &&
		(targetMapId !== currentMapId || targetLayerId !== entry.layerId);

	const handleSubmit = async () => {
		if (!canSubmit) return;
		const layer = targetLayers.find((l) => l.id === targetLayerId);
		if (!layer) {
			setError("Bitte eine Ebene wählen");
			return;
		}
		setSaving(true);
		setError(null);
		try {
			await onMove({
				mapId: targetMapId,
				layerId: layer.id,
				layerTitle: layer.title,
			});
			onClose();
		} catch (e) {
			setError(e instanceof Error ? e.message : "Verschieben fehlgeschlagen");
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			title="Eintrag verschieben"
			description={`„${entry.title}" in eine andere Ebene oder Karte legen.`}
		>
			<div className="mt-4 flex flex-col gap-3">
				<label className="flex flex-col gap-1.5 text-sm">
					<span className="text-[13px] font-medium text-fg">Zielkarte</span>
					<select
						className={selectClass}
						value={targetMapId}
						disabled={mapsLoading || saving}
						onChange={(e) => setTargetMapId(e.target.value)}
					>
						{maps.map((m) => (
							<option key={m.id} value={m.id}>
								{m.id === currentMapId ? `${m.title} (diese Karte)` : m.title}
							</option>
						))}
					</select>
				</label>
				<label className="flex flex-col gap-1.5 text-sm">
					<span className="text-[13px] font-medium text-fg">Zielebene</span>
					{layersLoading ? (
						<p className="text-xs text-fg-muted">Ebenen laden…</p>
					) : selectableLayers.length === 0 ? (
						<p className="text-xs text-fg-muted">
							{targetMapId === currentMapId
								? "Keine andere Ebene auf dieser Karte. Lege zuerst eine weitere Ebene an oder wähle eine andere Karte."
								: "Diese Karte hat keine Ebenen."}
						</p>
					) : (
						<select
							className={selectClass}
							value={targetLayerId}
							disabled={saving}
							onChange={(e) => setTargetLayerId(e.target.value)}
						>
							{selectableLayers.map((l) => (
								<option key={l.id} value={l.id}>
									{l.title}
								</option>
							))}
						</select>
					)}
				</label>
				{error ? (
					<p className="text-xs text-rose-600 dark:text-rose-400">{error}</p>
				) : null}
			</div>
			<DialogActions>
				<Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={saving}>
					Abbrechen
				</Button>
				<Button
					type="button"
					size="sm"
					disabled={!canSubmit}
					onClick={() => void handleSubmit()}
				>
					{saving ? "Verschieben…" : "Verschieben"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
